import { FC, useEffect, useMemo, useState } from 'react';
import { Dropdown, ExtendedComponentProps } from '@silverfort/sf-components';
import { LocalAccountsFilters } from '../../../../types/LocalAccountsFilters';
import { getLocalAccountsTableBaseFilters, updateHostnameOptions } from './utils';
import {
    getDropdownOptionsFromFilterOptions,
    getSortedFiltersDetails,
    isSearchableFilter,
    handleFiltersDetails,
    FilterSearchHandlers
} from '../../../../utils';
import { useGetLocalAccountsHostnames } from '../../../../api/queries/useGetLocalAccountsHostnames';
import { useLocalAccounts } from '../../../../context/LocalAccountsContext';
import { FilterDetails, FilterKeys, FilterValue, FilterValues } from '../../../../types/filters';

interface FilterItemProps {
    filterValue?: FilterValue<LocalAccountsFilters>;
    filterDetails: FilterDetails<LocalAccountsFilters>;
    onFilterChange: (
        filterId: FilterKeys<LocalAccountsFilters>,
        selectedValues?: FilterValue<LocalAccountsFilters>
    ) => void;
    filterSearchHandlers: FilterSearchHandlers<LocalAccountsFilters>;
}

type FilterSearchProps = Pick<
    ExtendedComponentProps<FilterValues<LocalAccountsFilters>>,
    'onRemove' | 'onChange' | 'searchHandler' | 'onCancel' | 'emptyStateText'
>;

type SearchStateProps = [
    FilterValue<LocalAccountsFilters> | undefined,
    ((value?: FilterValues<LocalAccountsFilters>) => void) | undefined,
    FilterSearchProps
];

const FilterItem: FC<FilterItemProps> = ({ filterValue, filterDetails, onFilterChange, filterSearchHandlers }) => {
    const [selectedItems, onItemClick, filterSearchProps]: SearchStateProps = useMemo(() => {
        if (isSearchableFilter(filterDetails)) {
            return [
                filterDetails.selectedItems,
                (value?: FilterValues<LocalAccountsFilters>) =>
                    value && filterSearchHandlers.handleFilterOptionClick(filterDetails.id, value),
                {
                    onRemove: () => filterSearchHandlers.onSearchableFilterRemove(filterDetails.id),
                    onChange: (value?: FilterValue<LocalAccountsFilters>) =>
                        filterSearchHandlers.onSearchableFilterChange(filterDetails.id, value),
                    searchHandler: (searchTerm: string) =>
                        filterSearchHandlers.setFilterSearchTerm(filterDetails.id, searchTerm),
                    onCancel: () => filterSearchHandlers.onSearchableFilterClose(filterDetails.id, filterValue),
                    emptyStateText: filterDetails.searchTerm ? 'No results found' : undefined
                }
            ];
        }

        return [
            filterValue,
            undefined,
            {
                onRemove: () => onFilterChange(filterDetails.id as FilterKeys<LocalAccountsFilters>, undefined),
                onChange: (value?: FilterValue<LocalAccountsFilters>) =>
                    onFilterChange(filterDetails.id as FilterKeys<LocalAccountsFilters>, value)
            }
        ];
    }, [filterDetails, filterValue, onFilterChange, filterSearchHandlers]);

    return (
        <Dropdown
            variant="filter"
            multiple={filterDetails.multiple}
            searchable={filterDetails.searchable}
            title={filterDetails.label}
            selectedItems={selectedItems || []}
            removable={!!selectedItems?.length}
            items={getDropdownOptionsFromFilterOptions(filterDetails.options, onItemClick)}
            width="232px"
            searchPlaceholder={filterDetails.searchPlaceholder}
            disableSelectAll={filterDetails.disableSelectAll}
            {...filterSearchProps}
        />
    );
};

const LocalAccountsTableFilters = () => {
    const { filters, setFilters } = useLocalAccounts();
    const [hostnamesSearchTerm, setHostnamesSearchTerm] = useState<string | undefined>(undefined);
    const { hostnames } = useGetLocalAccountsHostnames({ filters: { search: hostnamesSearchTerm } });
    const [filtersDetails, setFiltersDetails] = useState<FilterDetails<LocalAccountsFilters>[]>([]);

    useEffect(() => {
        setFiltersDetails(
            getLocalAccountsTableBaseFilters({
                hostnames: [],
                hostnamesSearchHandler: setHostnamesSearchTerm
            })
        );
    }, [setHostnamesSearchTerm]);

    useEffect(() => {
        updateHostnameOptions(hostnames ?? [], setFiltersDetails);
    }, [hostnames, setFiltersDetails]);

    const { setFilterValues, filterSearchHandlers } = useMemo(
        () =>
            handleFiltersDetails<LocalAccountsFilters>({
                setFilters,
                setFiltersDetails
            }),
        [setFilters, setFiltersDetails]
    );

    const sortedFilters = useMemo(() => getSortedFiltersDetails(filters, filtersDetails), [filters, filtersDetails]);

    return (
        <div className="flex gap-2">
            {sortedFilters.map((filterDetails) => (
                <FilterItem
                    key={filterDetails.id}
                    filterValue={filters[filterDetails.id]}
                    filterDetails={filterDetails}
                    onFilterChange={setFilterValues}
                    filterSearchHandlers={filterSearchHandlers}
                />
            ))}
        </div>
    );
};

export default LocalAccountsTableFilters;
